import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR, fmtDateTime } from "@/lib/format";
import { Upload, FileUp, CheckCircle2 } from "lucide-react";

const STATUS_CLS = {
  neu: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  geaendert: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
  unveraendert: "bg-muted text-muted-foreground border-border",
  fehler: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20",
};
const STATUS_LBL = { neu: "Neu", geaendert: "Geändert", unveraendert: "Unverändert", fehler: "Fehler" };

const FIELDS = {
  tariffs: ["name", "external_id", "internal_id", "tariff_type", "channel", "valid_from", "valid_to",
            "monthly_fee", "onetime_fee", "setup_fee", "data_volume", "voice", "sms", "min_term_months"],
  commission_rules: ["name", "commission_type", "valid_from", "valid_to", "amount", "percent", "base", "priority"],
};

export default function MfImport() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canImport = can(user?.role, "mobilfunk:import");
  const canApprove = can(user?.role, "mobilfunk:approve");
  const [importType, setImportType] = useState("tariffs");
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mapping, setMapping] = useState({});
  const fileRef = useRef(null);

  const { data: history } = useQuery({ queryKey: ["mf-imports"], queryFn: async () => (await api.get("/mobilfunk/imports")).data });

  const analyze = async () => {
    if (!file) return toast.error("Bitte Datei wählen");
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = rej; r.readAsDataURL(file);
      });
      const lower = file.name.toLowerCase();
      if (lower.endsWith(".pdf")) {
        const { data } = await api.post("/mobilfunk/imports/pdf-extract", { filename: file.name, content_base64: b64, import_type: importType });
        setJob(data); setMapping(data.mapping || {}); toast.success("KI-Vorschau aus PDF erstellt");
      } else {
        const ft = lower.endsWith(".xlsx") || lower.endsWith(".xls") ? "xlsx" : "csv";
        const { data } = await api.post("/mobilfunk/imports", { filename: file.name, content_base64: b64, file_type: ft, import_type: importType });
        setJob(data); setMapping(data.mapping || {}); toast.success("Vorschau erstellt");
      }
      qc.invalidateQueries({ queryKey: ["mf-imports"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remap = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      const { data } = await api.put(`/mobilfunk/imports/${job.id}/mapping`, clean);
      setJob(data); toast.success("Vorschau aktualisiert");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mobilfunk/imports/${job.id}/approve`);
      toast.success(`Übernommen: ${data.applied.neu} neu, ${data.applied.geaendert} geändert, ${data.applied.uebersprungen} übersprungen`);
      setJob(null); setFile(null); if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["mf-imports"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const c = job?.counts || {};
  return (
    <div data-testid="mf-import">
      <PageHeader title="Import-Center" description="Tarife und Provisionsmodelle aus CSV/Excel importieren – mit Vorschau, Dublettenprüfung und Freigabe." />
      <SectionCard title="Neuer Import" className="mb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1.5 sm:w-56">
            <Label>Importtyp</Label>
            <Select value={importType} onValueChange={setImportType}>
              <SelectTrigger data-testid="import-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="tariffs">Tarife</SelectItem><SelectItem value="commission_rules">Provisionsmodelle</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1.5">
            <Label>Datei (CSV / XLSX / PDF)</Label>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={!canImport}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm" data-testid="import-file-input" />
          </div>
          <Button onClick={analyze} disabled={!canImport || busy || !file} className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="import-analyze">
            <FileUp className="h-4 w-4" />Analysieren
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Erwartete Spalten (Tarife): name, external_id, valid_from, monthly_fee, data_volume, min_term_months. Kommazahlen und ; als Trenner werden erkannt. PDF-Dateien werden per KI (Claude) als Vorschau ausgelesen.</p>
      </SectionCard>

      {job && (job.columns || []).length > 0 && (
        <SectionCard title="Spaltenzuordnung" className="mb-4"
          action={<Button size="sm" variant="outline" onClick={remap} disabled={busy} data-testid="import-remap">Vorschau aktualisieren</Button>}>
          <p className="mb-3 text-xs text-muted-foreground">Leere Felder werden automatisch anhand des Spaltennamens erkannt. Weichen die Überschriften ab, ordnen Sie hier manuell zu.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(FIELDS[importType] || []).map((f) => (
              <div key={f} className="flex items-center gap-2" data-testid={`map-field-${f}`}>
                <Label className="w-36 shrink-0 text-xs">{f}</Label>
                <Select value={mapping[f] || "auto"} onValueChange={(v) => setMapping((m) => ({ ...m, [f]: v === "auto" ? "" : v }))}>
                  <SelectTrigger className="h-8"><SelectValue placeholder="auto" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">— automatisch —</SelectItem>
                    {job.columns.map((col) => <SelectItem key={col} value={col}>{col}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {job && (
        <SectionCard title="Vorschau & Abgleich" className="mb-4"
          action={<div className="flex items-center gap-1.5">
            <Badge className={STATUS_CLS.neu}>{c.neu || 0} neu</Badge>
            <Badge className={STATUS_CLS.geaendert}>{c.geaendert || 0} geändert</Badge>
            <Badge className={STATUS_CLS.unveraendert}>{c.unveraendert || 0} unverändert</Badge>
            <Badge className={STATUS_CLS.fehler}>{c.fehler || 0} Fehler</Badge>
          </div>}>
          <div className="space-y-2">
            {(job.preview || []).map((p) => (
              <div key={p.row} className="rounded-md border border-border p-3" data-testid="import-preview-row">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{p.head.name || "(ohne Name)"}</div>
                  <Badge className={STATUS_CLS[p.status]}>{STATUS_LBL[p.status]}</Badge>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {importType === "tariffs"
                    ? <>ab {p.version.valid_from || "—"} · {fmtEUR(p.version.monthly_fee)} · {p.version.data_volume || "—"}</>
                    : <>{p.version.commission_type} · {p.version.percent ? `${p.version.percent}%` : fmtEUR(p.version.amount)}</>}
                </div>
                {p.status === "geaendert" && Object.keys(p.diff || {}).length > 0 && (
                  <div className="mt-2 rounded bg-muted/40 p-2 text-xs">
                    {Object.entries(p.diff).map(([k, v]) => <div key={k}><span className="font-medium">{k}:</span> <span className="text-red-600 line-through">{String(v.alt)}</span> → <span className="text-emerald-600">{String(v.neu)}</span></div>)}
                  </div>
                )}
                {p.status === "fehler" && <div className="mt-1 text-xs text-red-600">{(p.diff?.errors || []).join(", ")}</div>}
              </div>
            ))}
          </div>
          {canApprove && (
            <div className="mt-4 flex items-center justify-end gap-3">
              <span className="text-xs text-muted-foreground">Übernahme legt neue/geänderte Datensätze als Entwurf an (Freigabe in Tarife/Provisionsmodelle).</span>
              <Button onClick={approve} disabled={busy} className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="import-approve"><CheckCircle2 className="h-4 w-4" />Übernehmen</Button>
            </div>
          )}
        </SectionCard>
      )}

      <SectionCard title="Importhistorie">
        {(history?.items || []).length === 0
          ? <EmptyState icon={Upload} title="Noch keine Importe" description="Importierte Dateien erscheinen hier mit Ergebnis." />
          : <div className="space-y-2">{history.items.map((h) => (
              <div key={h.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid="import-history-row">
                <div><div className="font-medium">{h.filename}</div><div className="text-xs text-muted-foreground">{h.import_type} · {fmtDateTime(h.created_at)} · {h.created_by}</div></div>
                <Badge variant={h.status === "uebernommen" ? "secondary" : "outline"}>{h.status === "uebernommen" ? `übernommen (${h.applied?.neu || 0}+${h.applied?.geaendert || 0})` : "Vorschau"}</Badge>
              </div>
            ))}</div>}
      </SectionCard>
    </div>
  );
}
